/**
 * Patient profile page
 * Refined design with golden accents matching calendar theme
 */

import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, User, History, GitBranch, FileText } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { PatientDocuments } from '@/components/patient/PatientDocuments'
import { PatientInfo } from '@/components/patient/PatientInfo'
import { PatientStats } from '@/components/patient/PatientStats'
import { PatientTimeline } from '@/components/patient/PatientTimeline'
import { PatientVisualTimeline } from '@/components/patient/PatientVisualTimeline'
import { usePatient, usePatientAppointments, useAppointmentStats } from '@/hooks/usePatient'

export function PatientPage() {
	const { id } = useParams<{ id: string }>()

	const { data: patient, isLoading: isLoadingPatient, error: patientError } = usePatient(id || '')
	const { data: appointments, isLoading: isLoadingAppointments } = usePatientAppointments(id || '')

	const stats = useAppointmentStats(appointments || [])

	if (isLoadingPatient) {
		return <LoadingState />
	}

	if (patientError || !patient) {
		return <ErrorState message={patientError?.message || 'Pacient nenalezen'} />
	}

	return (
		<div className="min-h-screen bg-[#faf8f5]">
			{/* Header */}
			<header className="border-b border-[#e8e4df] bg-white px-6 py-4 shadow-sm">
				<div className="mx-auto max-w-5xl flex items-center gap-4">
					<Link
						to="/calendar/zub"
						className="inline-flex items-center h-10 px-4 rounded-xl text-sm font-medium border border-[#e8e4df] bg-white text-[#5c5142] hover:bg-[#f5ebe0] hover:border-[#d7ad6c] transition-colors"
					>
						<ArrowLeft className="mr-2 h-4 w-4" />
						Zpět na kalendář
					</Link>
					<div className="flex items-center gap-3">
						<span className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#f5ebe0]">
							<User className="w-5 h-5 text-[#d7ad6c]" />
						</span>
						<h1 className="text-xl font-semibold text-[#3d3428]">Karta pacienta</h1>
					</div>
				</div>
			</header>

			{/* Content */}
			<div className="mx-auto max-w-5xl space-y-6 p-6">
				<PatientInfo patient={patient} />
				<PatientStats stats={stats} />

				{/* Tabs for History and Visual Timeline */}
				<Tabs defaultValue="history" className="w-full">
					<TabsList className="w-full justify-start bg-white border border-[#e8e4df] rounded-xl p-1 h-auto">
						<TabsTrigger
							value="history"
							className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-[#5c5142] data-[state=active]:bg-[#f5ebe0] data-[state=active]:text-[#3d3428] data-[state=active]:shadow-none transition-colors"
						>
							<History className="w-4 h-4" />
							Historie
						</TabsTrigger>
						<TabsTrigger
							value="timeline"
							className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-[#5c5142] data-[state=active]:bg-[#f5ebe0] data-[state=active]:text-[#3d3428] data-[state=active]:shadow-none transition-colors"
						>
							<GitBranch className="w-4 h-4" />
							Časová osa
						</TabsTrigger>
						<TabsTrigger
							value="documents"
							className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-[#5c5142] data-[state=active]:bg-[#f5ebe0] data-[state=active]:text-[#3d3428] data-[state=active]:shadow-none transition-colors"
						>
							<FileText className="w-4 h-4" />
							Dokumenty
						</TabsTrigger>
					</TabsList>

					<TabsContent value="history" className="mt-4">
						{isLoadingAppointments ? (
							<div className="rounded-xl bg-white p-6 shadow-sm border border-[#e8e4df]">
								<div className="flex items-center justify-center py-8">
									<div className="h-6 w-6 animate-spin rounded-full border-2 border-[#d7ad6c] border-t-transparent" />
									<span className="ml-3 text-[#8c8275]">
										Načítání historie termínů...
									</span>
								</div>
							</div>
						) : (
							<PatientTimeline appointments={appointments || []} />
						)}
					</TabsContent>

					<TabsContent value="timeline" className="mt-4">
						{isLoadingAppointments ? (
							<div className="rounded-xl bg-white p-6 shadow-sm border border-[#e8e4df]">
								<div className="flex items-center justify-center py-8">
									<div className="h-6 w-6 animate-spin rounded-full border-2 border-[#d7ad6c] border-t-transparent" />
									<span className="ml-3 text-[#8c8275]">
										Načítání časové osy...
									</span>
								</div>
							</div>
						) : (
							<PatientVisualTimeline appointments={appointments || []} />
						)}
					</TabsContent>

					<TabsContent value="documents" className="mt-4">
						<PatientDocuments patientId={patient.id} />
					</TabsContent>
				</Tabs>
			</div>
		</div>
	)
}

function LoadingState() {
	return (
		<div className="flex min-h-screen items-center justify-center bg-[#faf8f5]">
			<div className="flex flex-col items-center gap-4">
				<div className="h-10 w-10 animate-spin rounded-full border-3 border-[#d7ad6c] border-t-transparent" />
				<span className="text-lg text-[#5c5142]">Načítání pacienta...</span>
			</div>
		</div>
	)
}

function ErrorState({ message }: { message: string }) {
	return (
		<div className="flex min-h-screen flex-col items-center justify-center bg-[#faf8f5]">
			<div className="rounded-xl bg-white p-8 shadow-sm border border-[#e8e4df] text-center max-w-md">
				<div className="mx-auto mb-4 flex items-center justify-center w-14 h-14 rounded-xl bg-red-50">
					<span className="text-2xl">⚠️</span>
				</div>
				<h2 className="mb-2 text-xl font-semibold text-[#3d3428]">Chyba</h2>
				<p className="mb-6 text-[#8c8275]">{message}</p>
				<Link
					to="/calendar/zub"
					className="inline-flex items-center h-11 px-6 rounded-xl text-sm font-medium bg-[#d7ad6c] text-white hover:bg-[#c49b5a] transition-colors shadow-sm"
				>
					<ArrowLeft className="mr-2 h-4 w-4" />
					Zpět na kalendář
				</Link>
			</div>
		</div>
	)
}
